import { useSyncExternalStore } from "react";

/** One DOM measurement of a drei <Scroll html> overlay. All values in px.
 *  `anchors` maps each [data-scroll-anchor] key to its top relative to the
 *  [data-scroll-content] root. */
export type ScrollMeasurement = {
  vh: number;
  contentHeight: number;
  anchors: Record<string, number>;
};

export type ScrollLayoutStore = {
  get: () => ScrollMeasurement | null;
  set: (m: ScrollMeasurement | null) => void;
  subscribe: (cb: () => void) => () => void;
};

const near = (a: number, b: number, eps: number) => Math.abs(a - b) <= eps;

/** Tolerant equality so sub-pixel/layout jitter never churns subscribers
 *  (every store update re-renders scene consumers and may remount drei's
 *  scroll container via the pages prop). */
export function measurementsEqual(
  a: ScrollMeasurement | null,
  b: ScrollMeasurement | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (!near(a.vh, b.vh, 1) || !near(a.contentHeight, b.contentHeight, 8)) return false;
  const ak = Object.keys(a.anchors);
  if (ak.length !== Object.keys(b.anchors).length) return false;
  return ak.every((k) => k in b.anchors && near(a.anchors[k], b.anchors[k], 4));
}

export function createScrollLayoutStore(): ScrollLayoutStore {
  let current: ScrollMeasurement | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set: (m) => {
      if (measurementsEqual(current, m)) return;
      current = m;
      listeners.forEach((l) => l());
    },
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** pages so the overlay's last screen lands exactly at offset=1:
 *  drei translates HTML by (pages−1)·vh·offset ⟹ pages = contentHeight/vh. */
export function pagesFromMeasurement(
  m: ScrollMeasurement | null,
  fallback: number,
): number {
  if (!m || m.vh <= 0) return fallback;
  return Math.max(1, m.contentHeight / m.vh);
}

export function useScrollLayout(store: ScrollLayoutStore): ScrollMeasurement | null {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

// Module singletons (NOT React Context): drei's <Scroll html> mounts a
// detached React root, and SideNavOverlay lives in the app root — a module
// store is the only thing all three trees can share.
export const homeScrollLayout = createScrollLayoutStore();
export const decksScrollLayout = createScrollLayoutStore();
export const radioScrollLayout = createScrollLayoutStore();
